/**
 * Equipment and titles — cosmetics earned by learning (`docs/rpg.md` §19, §31).
 *
 * **Nothing in this codebase reads equipment.** Not difficulty, not XP, not
 * recommendations, not review scheduling, not access to a lesson or a practice
 * page. An item changes one thing, the drawing of the avatar, and §19 states
 * the rule this file exists to keep: *avoid creating a pay-to-win system*. A
 * student wearing the Infinity Staff and a student wearing nothing at all are
 * handed exactly the same mathematics, in exactly the same order, at exactly
 * the same difficulty. If a future change wants to read `rpg.equipped` for any
 * reason other than drawing a picture, that is the change to refuse.
 *
 * §44 rules out the rest of the shape this feature usually takes. There is no
 * money here — gold is earned only from quest and boss rewards, never bought
 * and never sold — no loot box, no randomness of any kind (`packages/world`
 * sits below `apps/web` and the no-randomness rule applies), and no timed
 * offer. Every item's condition is stated in advance, in full, on the tile: a
 * student can read what the Algebra Staff costs them before they spend a
 * minute on it, and the answer is always "learn some algebra".
 *
 * **Earned is not owned.** An item whose condition is met is `claimable`, and
 * the student's own tap is what puts it in `inventory` — the availability of
 * every earned item is derived from the profile on every render, exactly as
 * quest progress is (`docs/rpg-implementation-plan.md` §0.4), and nothing is
 * written to the profile until the student asks for it. The one stored fact is
 * that they claimed it, which is what makes a claim idempotent.
 *
 * The definitions live in `content/src/world/items.ts`, above this package, and
 * arrive as parameters — the same arrangement as kingdoms, quests and bosses.
 */

import type { ConceptGraph, StrandId } from '@yuan-math/curriculum';
import {
  equipCosmetic,
  grantItem,
  isMastered,
  spendGold,
  unequipCosmetic,
  type LearnerProfile,
} from '@yuan-math/learning';

/* -------------------------------------------------------------------------- */
/* Definitions                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Where an item is worn. Three slots, and three is the whole list.
 *
 * §19 names swords, shields, staffs, bows, helmets, capes, backpacks and pets.
 * All but the last are one of these three — a staff and a shield are both a
 * thing in the hands, a helmet and a circlet are both a thing on the head — and
 * pets are deliberately *not* a fourth slot here. §20 calls them optional, the
 * plan holds them back to after the MVP, and a pet is a companion drawing with
 * its own behaviour rather than a hat; adding an empty slot now would be a
 * promise in the type system that nothing keeps.
 */
export type ItemSlot = 'held' | 'cape' | 'headgear';

/** Every slot, in the order a wardrobe should group them. */
export const ITEM_SLOTS: readonly ItemSlot[] = ['held', 'cape', 'headgear'];

/**
 * What an earned item asks for.
 *
 * These mirror the conditions `ACHIEVEMENTS` in `packages/learning` already
 * checks — a count of mastered concepts, a thing cleared, a count of rewards
 * claimed — but as *data* rather than as a predicate, because an item id and a
 * strand id both come out of `content/` and a mistyped one has to be catchable
 * by `validateItems` rather than by a student never seeing the item.
 *
 * Every variant is a question the profile can already answer, so availability
 * is derived and nothing about an unclaimed item is stored.
 */
export type ItemCondition =
  /**
   * Master `count` concepts in a strand. Mastery proper — earned by practice,
   * six attempts minimum — never `knownSet`: §19's signature pieces say
   * "Master Algebra", and a placement estimate is not mastery (`CLAUDE.md`).
   * A staff handed out for a diagnostic answer would be exactly the pay-to-win
   * shortcut the section warns about, in the one currency that matters here.
   */
  | { kind: 'master-concepts-in-strand'; strandId: StrandId; count: number }
  /** Beat a named boss. The kingdom trophies. */
  | { kind: 'clear-boss'; bossId: string }
  /**
   * Claim `count` story quest rewards. Claimed rather than completed, for the
   * reason `clear-quests` objectives count claims: claiming is the record that
   * a quest was seen through to the end.
   */
  | { kind: 'claim-quests'; count: number }
  /**
   * Earn a named achievement from `ACHIEVEMENTS` in `packages/learning`.
   *
   * The key the offshore isles are opened with (`isles.ts`), and the reason
   * this variant exists: an isle's cosmetic must be come by the *same* way
   * every other cosmetic is, or the archipelago becomes a second entitlement
   * path with its own rules — the mistake `subscriptions` has no insert policy
   * to prevent, one layer down. So the isle is where the reward is presented
   * and the achievement is what actually grants it, exactly as a quest page
   * presents work that `recordAttempt` is what actually credits.
   *
   * An achievement is never revoked (`mergeProfiles` keeps the earliest unlock
   * time), so unlike a mastery count this condition cannot come undone — which
   * is what makes it a fair thing to hang a place on.
   */
  | { kind: 'earn-achievement'; achievementId: string };

/**
 * How an item is come by. A tagged union, so there is no third way in and no
 * item that is quietly both.
 *
 * `earned` is the important one and most of the catalogue is it. `shop` exists
 * so gold has somewhere to go — a currency that pays for nothing is a number
 * that quietly means "how much have you ground", which §47 is against — and it
 * is priced in the same modest range a couple of quests pay.
 *
 * There is no third variant, and in particular no real-money one. Payments buy
 * volume of generated practice and nothing else (`CLAUDE.md`); a cosmetic
 * behind a card number would put a visible mark of who paid on a child's
 * avatar, which is §44's first paragraph.
 */
export type ItemSource =
  | { kind: 'earned'; condition: ItemCondition }
  | { kind: 'shop'; gold: number };

/**
 * Which drawing an item is.
 *
 * Separate from `id` because they answer to different things: the id is the
 * content author's, stored in a student's profile forever and never renamed,
 * while the art names one of a fixed set of hand-drawn SVG layers in
 * `packages/ui`. That package cannot import this one — it sits beside
 * `apps/web`, not below it — so the union is mirrored there structurally, the
 * same arrangement `AvatarConfig` already has. `validateItems` checks the art
 * matches the slot, which is the mistake worth catching: a cape drawn in the
 * hands.
 */
export type ItemArt =
  | 'algebra-staff'
  | 'geometry-shield'
  | 'infinity-staff'
  | 'compass'
  | 'abacus'
  | 'gatekeeper-cape'
  | 'dragonscale-cape'
  | 'traveller-cape'
  | 'starlit-cape'
  | 'circlet'
  | 'scholar-cap'
  | 'sun-hat'
  /* The three isle pieces. See `content/src/world/isles.ts`. */
  | 'tide-hat'
  | 'keepers-lamp'
  | 'tally-cape'
  | 'north-parka'
  | 'sun-shawl';

/** The slot each drawing is drawn for. The one table the art and the data share. */
export const ART_SLOT: Readonly<Record<ItemArt, ItemSlot>> = {
  'algebra-staff': 'held',
  'geometry-shield': 'held',
  'infinity-staff': 'held',
  compass: 'held',
  abacus: 'held',
  'gatekeeper-cape': 'cape',
  'dragonscale-cape': 'cape',
  'traveller-cape': 'cape',
  'starlit-cape': 'cape',
  circlet: 'headgear',
  'scholar-cap': 'headgear',
  'sun-hat': 'headgear',
  'tide-hat': 'headgear',
  'keepers-lamp': 'held',
  'tally-cape': 'cape',
  'north-parka': 'cape',
  'sun-shawl': 'cape',
};

export type ItemDef = {
  id: string;
  /** The name on the tile. "Algebra Staff". */
  name: string;
  /** The badge face, in data so the UI stays generic — as `Achievement.icon` is. */
  emoji: string;
  slot: ItemSlot;
  /** One sentence, written for a student. Flavour, never a claim about ability. */
  flavor: string;
  art: ItemArt;
  source: ItemSource;
};

/**
 * An honorific.
 *
 * Titles are §31's cheapest reward and its best: a word, carried on the home
 * screen, that costs nothing and cannot be traded. Two ladders, deliberately
 * kept apart — `deriveTitle` reads the *game* level, which is XP and habit, and
 * these are earned from named deeds. Neither is a claim about mathematics, and
 * mastery is shown above both wherever they appear (`CLAUDE.md` §10).
 */
export type TitleDef = {
  id: string;
  /** The honorific itself. "Dragonsbane". */
  text: string;
  condition: ItemCondition;
};

/* -------------------------------------------------------------------------- */
/* Availability                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Where a student stands with one item.
 *
 * `locked` — an earned item whose condition is unmet, or a shop item they
 * cannot afford yet. Honest either way, and the page says which.
 * `claimable` — theirs for a tap: the condition is met, or the gold is there.
 * `owned` — in the inventory, not currently worn.
 * `equipped` — worn in its slot.
 */
export type ItemAvailability = 'locked' | 'claimable' | 'owned' | 'equipped';

/**
 * How far through an item's condition a student is.
 *
 * Counted rather than rounded into a percentage, for the reason the adventure
 * home page counts prerequisites: "7 of 10 Algebra skills" survives being true
 * in a way "70%" does not.
 */
export type ConditionProgress = { done: number; total: number };

/**
 * The state of one item, derived. Nothing about an unclaimed item is stored.
 *
 * `graph` is here because a strand condition is a question about the curriculum
 * — which concepts are algebra — and `packages/world` sits above `curriculum`
 * and below `content`, so the graph arrives as a parameter like everything else
 * in this package. `now` is here because mastery decays.
 */
export function itemAvailability(
  def: ItemDef,
  profile: LearnerProfile,
  graph: ConceptGraph,
  now: number,
): ItemAvailability {
  const rpg = profile.rpg;
  if (rpg?.equipped[def.slot] === def.id) return 'equipped';
  if (rpg?.inventory.includes(def.id)) return 'owned';
  if (def.source.kind === 'shop') return (rpg?.gold ?? 0) >= def.source.gold ? 'claimable' : 'locked';
  return conditionMet(def.source.condition, profile, graph, now) ? 'claimable' : 'locked';
}

/** How much of an earned item's condition is behind the student. */
export function conditionProgress(
  condition: ItemCondition,
  profile: LearnerProfile,
  graph: ConceptGraph,
  now: number,
): ConditionProgress {
  switch (condition.kind) {
    case 'master-concepts-in-strand':
      return { done: masteredInStrand(condition.strandId, profile, graph, now), total: condition.count };
    case 'clear-boss':
      return { done: (profile.rpg?.clearedBossIds ?? []).includes(condition.bossId) ? 1 : 0, total: 1 };
    case 'claim-quests':
      return { done: (profile.rpg?.claimedQuestIds ?? []).length, total: condition.count };
    case 'earn-achievement':
      return {
        done: profile.achievements.some((entry) => entry.id === condition.achievementId) ? 1 : 0,
        total: 1,
      };
  }
}

/** Whether a condition is satisfied. The same count, read as a yes or a no. */
export function conditionMet(
  condition: ItemCondition,
  profile: LearnerProfile,
  graph: ConceptGraph,
  now: number,
): boolean {
  const { done, total } = conditionProgress(condition, profile, graph, now);
  return done >= total;
}

/**
 * Names a condition asks about, resolved by the caller.
 *
 * `apps/web` holds the registry and the boss list; this package holds neither.
 * A resolver that returns `undefined` falls back to the raw id, which is ugly
 * and readable, and is what a mistyped id looks like on screen — but
 * `validateItems` means it never ships.
 */
export type ConditionNames = {
  strand?(id: StrandId): string | undefined;
  boss?(id: string): string | undefined;
  achievement?(id: string): string | undefined;
};

/**
 * The condition, in a student's words: "Master 10 Algebra skills".
 *
 * The honest sentence §0.6 asks for. A locked item says exactly what would
 * unlock it, in the platform's own vocabulary, with no "keep going!" in place
 * of the requirement — a tile that will not say what it wants is asking a child
 * to guess.
 */
export function conditionLabel(condition: ItemCondition, names: ConditionNames = {}): string {
  switch (condition.kind) {
    case 'master-concepts-in-strand': {
      const strand = names.strand?.(condition.strandId) ?? condition.strandId;
      return `Master ${condition.count} ${strand} ${condition.count === 1 ? 'skill' : 'skills'}`;
    }
    case 'clear-boss':
      return `Defeat ${names.boss?.(condition.bossId) ?? condition.bossId}`;
    case 'claim-quests':
      return `Finish ${condition.count} ${condition.count === 1 ? 'quest' : 'quests'}`;
    case 'earn-achievement':
      // The badge's own title, so the sentence on a shut isle is the sentence
      // on the achievements page and a student is looking for one thing.
      return `Earn ${names.achievement?.(condition.achievementId) ?? condition.achievementId}`;
  }
}

/* -------------------------------------------------------------------------- */
/* Reducers                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Claims an earned item into the inventory, once.
 *
 * Three refusals, and all three matter: a shop item cannot be claimed for free,
 * an unmet condition cannot be claimed early, and an item already owned is
 * returned untouched rather than appended twice. The last is what makes a
 * double-tap, a stale render and a two-device merge all harmless — the same
 * construction as `claimQuestReward`, and the reason `grantItem` is idempotent
 * underneath as well.
 *
 * Nothing else moves. No XP, no gold, no mastery, no streak, no daily quest: a
 * cosmetic is a cosmetic, and the learning that earned it was already paid for
 * when it happened.
 */
export function claimItem(
  profile: LearnerProfile,
  def: ItemDef,
  graph: ConceptGraph,
  now: number,
): LearnerProfile {
  if (def.source.kind !== 'earned') return profile;
  if (!conditionMet(def.source.condition, profile, graph, now)) return profile;
  return grantItem(profile, { itemId: def.id });
}

/**
 * Buys a shop item.
 *
 * Straight through `spendGold`, which already refuses an overdraft, a
 * non-positive price and a second sale of the same cosmetic — so this adds one
 * rule of its own, that an earned item has no price and cannot be bought around
 * its condition. Paying gold for the Algebra Staff would make the algebra
 * optional, which is the whole of what §19 says not to build.
 */
export function buyItem(profile: LearnerProfile, def: ItemDef): LearnerProfile {
  if (def.source.kind !== 'shop') return profile;
  return spendGold(profile, { amount: def.source.gold, itemId: def.id });
}

/**
 * Wears an item, replacing whatever was in its slot.
 *
 * Refuses anything unowned, in `equipCosmetic` underneath: `equipped` is a
 * projection of `inventory`, and a slot naming an item the student never
 * claimed would draw a cosmetic out of nothing after a merge dropped it.
 */
export function equipItem(profile: LearnerProfile, def: ItemDef): LearnerProfile {
  return equipCosmetic(profile, { slot: def.slot, itemId: def.id });
}

/** Takes an item off. By slot, because that is what "take the cape off" means. */
export function unequipItem(profile: LearnerProfile, slot: ItemSlot): LearnerProfile {
  return unequipCosmetic(profile, { slot });
}

/* -------------------------------------------------------------------------- */
/* Reading the wardrobe                                                        */
/* -------------------------------------------------------------------------- */

/** What the student is wearing, slot → definition, skipping ids nothing defines. */
export function equippedItems(
  items: readonly ItemDef[],
  profile: LearnerProfile,
): Partial<Record<ItemSlot, ItemDef>> {
  const byId = new Map(items.map((item) => [item.id, item]));
  const out: Partial<Record<ItemSlot, ItemDef>> = {};
  for (const slot of ITEM_SLOTS) {
    const id = profile.rpg?.equipped[slot];
    const def = id === undefined ? undefined : byId.get(id);
    // An item worn in the wrong slot is a corrupt profile, not a look: honour
    // the slot the definition names, so the drawing can never contradict it.
    if (def && def.slot === slot) out[slot] = def;
  }
  return out;
}

/* -------------------------------------------------------------------------- */
/* Titles                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The flavour ladder, by game level.
 *
 * Six rungs over the levels a student actually reaches, and a separate ladder
 * from `levelForXp().title` on purpose. That one is the platform's own voice —
 * "Apprentice", "Analyst", "Mathematician" — and belongs beside the XP bar on
 * the dashboard; this one is the adventure's, and belongs in a kingdom. Both
 * are read off XP, which measures habit and not ability (`docs/rpg.md` §46),
 * and neither may ever be the thing a student is judged by.
 *
 * The rungs land where the levels do rather than at round numbers: level 5 is
 * 500 XP and a few weeks in, level 20 is 9,500 and a serious stretch of the
 * curriculum, so "Master of the Realms" is somewhere real.
 */
const TITLE_LADDER: readonly { minLevel: number; text: string }[] = [
  { minLevel: 20, text: 'Master of the Realms' },
  { minLevel: 15, text: 'Realm Warden' },
  { minLevel: 10, text: 'Kingdom Champion' },
  { minLevel: 6, text: 'Trailblazer' },
  { minLevel: 3, text: 'Wayfarer' },
  { minLevel: 1, text: 'Novice Adventurer' },
];

/** The adventurer's title at this game level. Never empty — everyone is something. */
export function deriveTitle(level: number): string {
  const rung = TITLE_LADDER.find((entry) => level >= entry.minLevel);
  return rung?.text ?? 'Novice Adventurer';
}

/**
 * The honorifics a student has actually earned, in catalogue order.
 *
 * Derived like everything else, and never revoked: the conditions that award a
 * title are deeds — a boss down, a chain of quests finished — and a deed does
 * not un-happen. A title on a mastery count could be taken back by a fortnight
 * of not practising, which is why the catalogue does not write one.
 */
export function earnedTitles(
  titles: readonly TitleDef[],
  profile: LearnerProfile,
  graph: ConceptGraph,
  now: number,
): TitleDef[] {
  return titles.filter((title) => conditionMet(title.condition, profile, graph, now));
}

/* -------------------------------------------------------------------------- */

function masteredInStrand(
  strandId: StrandId,
  profile: LearnerProfile,
  graph: ConceptGraph,
  now: number,
): number {
  let count = 0;
  for (const [conceptId, state] of Object.entries(profile.concepts)) {
    if (graph.concepts.get(conceptId)?.strand !== strandId) continue;
    if (isMastered(state, now)) count += 1;
  }
  return count;
}
